import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import AdminPanel from '../admin/AdminPanel';
import SystemOverview from '../admin/SystemOverview';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import EventModal from '../calendar/EventModal';
import ScheduleManager from '../schedule/ScheduleManager';
import UnifiedBookingsTab from '../booking/UnifiedBookingsTab';
import ClientManager from '../client/ClientManager';
import ExerciseManager from '../exercise/ExerciseManager';
import ExerciseDetailModal from '../exercise/ExerciseDetailModal';
import ExerciseEditModal from '../exercise/ExerciseEditModal';
import MuscleGroupSelector from '../exercise/MuscleGroupSelector';
import WorkoutTemplateLibrary from '../workout/WorkoutTemplateLibrary';
import WorkoutInstancesExplorer from '../workout/WorkoutInstancesExplorer';
import AdminCalendar from '../schedule/AdminCalendar';
import WorkoutViewModal from '../workout/WorkoutViewModal';
import AccountingDashboard from '../accounting/AccountingDashboard';
import GhostDataCleanup from '../admin/GhostDataCleanup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrainerDashboard = ({ user, onLogout, language, t }) => {
  // Check sessionStorage for initial tab (for context-aware navigation)
  const getInitialTab = () => {
    const storedTab = sessionStorage.getItem('activeTab');
    if (storedTab) {
      sessionStorage.removeItem('activeTab'); // Clear after reading
      // Legacy tab names (Feed removed) → Hoy cockpit
      return (storedTab === 'activity' || storedTab === 'feed') ? 'hoy' : storedTab;
    }
    // Operational Cockpit: trainers/admins land on the "Hoy" decision surface
    return 'hoy';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [timeSlots, setTimeSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [clients, setClients] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [selectedExerciseForDetail, setSelectedExerciseForDetail] = useState(null);
  const [editingExercise, setEditingExercise] = useState(null);
  const [addingExercise, setAddingExercise] = useState(false);
  const [birthdayClients, setBirthdayClients] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  // True when the schedule data (slots/bookings) failed to load after retries,
  // so we can show an honest "couldn't load" state instead of a calendar that
  // looks empty — a transient 503 pod flap must not masquerade as "0 slots".
  const [calendarError, setCalendarError] = useState(false);
  
  // State for navigating from Bookings to Planner
  const [selectedClientForPlanner, setSelectedClientForPlanner] = useState(null);
  
  // Workout view modal state
  const [viewingWorkout, setViewingWorkout] = useState(null);
  const [viewingWorkoutBooking, setViewingWorkoutBooking] = useState(null);
  const [viewingWorkoutPRs, setViewingWorkoutPRs] = useState([]);

  // Get unique muscle groups from all exercises
  const allMuscleGroups = React.useMemo(() => {
    const muscles = new Set();
    exercises.forEach(ex => {
      if (ex.muscle_groups && Array.isArray(ex.muscle_groups)) {
        ex.muscle_groups.forEach(muscle => muscles.add(muscle));
      }
    });
    return Array.from(muscles).sort();
  }, [exercises]);

  useEffect(() => {
    fetchData();
    fetchBirthdays();
  }, []);

  // GET with retry/backoff for transient failures (network error, timeout,
  // 5xx/503). A tier_0 single-worker pod briefly fails its /health probe and
  // leaves the Service, so one-shot calls intermittently reject — retrying
  // rides over the blip. Real 4xx (auth/permission) are NOT retried.
  const getWithRetry = async (url, headers, tries = 3) => {
    let lastErr;
    for (let i = 0; i < tries; i++) {
      try {
        return await axios.get(url, { headers, timeout: 20000 });
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        if (status && status >= 400 && status < 500) throw err;
        if (i < tries - 1) await new Promise((r) => setTimeout(r, 800 * (i + 1)));
      }
    }
    throw lastErr;
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch timeslots with a 3-month window to avoid hitting result limits
      const now = new Date();
      const dateFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      const dateTo = new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString().split('T')[0];
      
      const results = await Promise.allSettled([
        getWithRetry(`${API}/timeslots?date_from=${dateFrom}&date_to=${dateTo}`, headers),
        getWithRetry(`${API}/bookings?limit=500&date_range=all`, headers),
        getWithRetry(`${API}/users/clients`, headers),
        getWithRetry(`${API}/exercises/list`, headers),
        // Slim booking-index instead of the full (~1.3MB) workouts payload.
        // The calendar only needs which bookings have a linked workout.
        getWithRetry(`${API}/workouts/booking-index?date_from=${dateFrom}&date_to=${dateTo}`, headers)
      ]);
      
      // Extract successful responses, use empty arrays for failed ones
      const [slotsRes, bookingsRes, clientsRes, exercisesRes, workoutsRes] = results;
      
      if (slotsRes.status === 'fulfilled') {
        setTimeSlots(slotsRes.value.data);
      } else {
        // Transient failure after retries — keep last-known slots instead of
        // blanking the calendar (which would look like "no slots exist").
        console.warn('Failed to fetch timeslots:', slotsRes.reason);
      }
      
      if (bookingsRes.status === 'fulfilled') {
        // Handle both paginated response and direct array response
        const bookingsData = bookingsRes.value.data;
        if (Array.isArray(bookingsData)) {
          setBookings(bookingsData);
        } else if (bookingsData && Array.isArray(bookingsData.bookings)) {
          setBookings(bookingsData.bookings);
        } else {
          setBookings([]);
        }
      } else {
        console.warn('Failed to fetch bookings:', bookingsRes.reason);
      }

      // Surface an honest error state only when the schedule data itself failed.
      setCalendarError(slotsRes.status !== 'fulfilled' || bookingsRes.status !== 'fulfilled');
      
      if (clientsRes.status === 'fulfilled') {
        setClients(clientsRes.value.data || []);
      } else {
        console.warn('Failed to fetch clients:', clientsRes.reason);
      }
      
      if (exercisesRes.status === 'fulfilled') {
        setExercises(exercisesRes.value.data);
      } else {
        console.error('Failed to fetch exercises:', exercisesRes.reason);
      }
      
      if (workoutsRes.status === 'fulfilled') {
        setWorkouts(workoutsRes.value.data);
      } else {
        console.warn('Failed to fetch workouts:', workoutsRes.reason);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setCalendarError(true);
    }
  };

  // Lightweight refresh for calendar/booking actions — only re-fetches the
  // data those views actually mutate (timeslots + bookings + clients). Avoids
  // re-downloading the ~6.6MB exercise library and ~1.3MB workouts list, which
  // is what made adding a client to a slot take several seconds.
  const refreshCalendarData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const now = new Date();
      const dateFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      const dateTo = new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString().split('T')[0];

      const [slotsRes, bookingsRes, clientsRes] = await Promise.allSettled([
        getWithRetry(`${API}/timeslots?date_from=${dateFrom}&date_to=${dateTo}`, headers),
        getWithRetry(`${API}/bookings?limit=500&date_range=all`, headers),
        getWithRetry(`${API}/users/clients`, headers),
      ]);

      if (slotsRes.status === 'fulfilled') setTimeSlots(slotsRes.value.data);
      if (bookingsRes.status === 'fulfilled') {
        const d = bookingsRes.value.data;
        setBookings(Array.isArray(d) ? d : (Array.isArray(d?.bookings) ? d.bookings : []));
      }
      if (clientsRes.status === 'fulfilled') setClients(clientsRes.value.data || []);
      setCalendarError(slotsRes.status !== 'fulfilled' || bookingsRes.status !== 'fulfilled');
    } catch (error) {
      console.error('Error refreshing calendar data:', error);
    }
  };

  const fetchBirthdays = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/users/birthdays-today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBirthdayClients(res.data || []);
    } catch (error) {
      console.error('Error fetching birthdays:', error);
    }
  };

  const createTimeSlot = async (slotData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/timeslots`, slotData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Error creating time slot:', error);
    }
  };

  // Updated to handle full booking updates including date/time changes
  const updateBooking = async (bookingId, status, notes = '', fullUpdate = null) => {
    try {
      const token = localStorage.getItem('token');
      const updateData = fullUpdate || { status, trainer_notes: notes };
      await axios.put(`${API}/bookings/${bookingId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchData(); // Wait for data to refresh
    } catch (error) {
      console.error('Error updating booking:', error);
      const errorMsg = error.response?.data?.detail || 'Unknown error';
      alert('❌ Error updating booking: ' + errorMsg);
    }
  };

  const editExercise = async (exercise) => {
    // The library grid uses the slim list payload — fetch full detail so the
    // edit modal has execution steps, muscles, benefits, etc.
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/exercises/${exercise.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingExercise({ ...exercise, ...res.data });
    } catch (error) {
      console.error('Failed to load full exercise for editing:', error);
      setEditingExercise(exercise); // fallback to slim
    }
  };

  const handleEditExercise = async (exerciseData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/exercises/${editingExercise.id}`, exerciseData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingExercise(null);
      fetchData();
    } catch (error) {
      console.error('Error updating exercise:', error);
    }
  };

  const handleCreateExercise = async (exerciseData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/exercises`, exerciseData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddingExercise(false);
      fetchData();
    } catch (error) {
      console.error('Error creating exercise:', error);
      alert('Error creating exercise: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900">
      <nav className="glass-nav p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_workout-central-9/artifacts/1boxowhf_logo%20btc.PNG" 
              alt="Bétera Training Center" 
              className="h-10 w-10 object-contain"
            />
            <h1 className="text-2xl font-bold text-white">Betera Training Center</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => window.open('/live-gym', '_blank')}
              className="px-3 py-2 bg-green-500/20 text-green-300 rounded-lg hover:bg-green-500/30 transition flex items-center gap-2"
              title="Open Live Gym Board (for TV display)"
              data-testid="live-gym-button"
            >
              <span>📺</span>
              <span className="hidden sm:inline">Live Board</span>
            </button>
            <span className="text-blue-200">{t('welcome')}, {user.full_name}</span>
            <span className={`badge ${
              user.role === 'administrator' 
                ? 'bg-red-500/20 text-red-200' 
                : user.role === 'trainer'
                ? 'bg-yellow-500/20 text-yellow-200'
                : 'bg-blue-500/20 text-blue-200'
            }`}>
              {user.role === 'administrator' ? t('administrator') : user.role === 'trainer' ? t('trainer') : t('client')}
            </span>
            <button onClick={onLogout} className="btn-secondary">
              {t('logout')}
            </button>
          </div>
        </div>
      </nav>

      <div className={`mx-auto p-6 ${activeTab === 'calendar' ? 'max-w-[1800px] px-4 lg:px-8' : 'max-w-7xl'}`}>
        {/* Birthday Alert Banner */}
        {birthdayClients.length > 0 && (
          <div className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-400/40 rounded-lg p-4 mb-6 animate-pulse">
            <div className="flex items-center gap-3">
              <span className="text-4xl">🎂</span>
              <div className="flex-1">
                <h3 className="text-white font-semibold text-lg mb-1">
                  🎉 {t('birthday_alert')} 🎉
                </h3>
                <div className="flex flex-wrap gap-2">
                  {birthdayClients.map((client) => (
                    <span key={client.id} className="bg-pink-500/30 text-white px-3 py-1 rounded-full text-sm">
                      {client.full_name} {client.age ? `(${client.age} ${t('years')})` : ''}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center gap-3 border-b border-blue-400/20 pb-4 overflow-x-auto">
            {(() => {
              const LABELS = {
                hoy: '🏠 Today', calendar: '📅 Calendar', bookings: '📋 Bookings', clients: '👥 Clients',
                templates: '📑 Templates', exercises: '🏋️ Exercises', workouts: '💪 Workouts',
                accounting: '💰 Accounting', system: '🔧 System', cleanup: '🧹 Cleanup',
              };
              const isAdmin = user?.role === 'administrator';
              const primary = ['hoy', 'calendar', 'bookings', 'clients', 'exercises', 'workouts', 'accounting'];
              const secondary = ['templates',
                ...(isAdmin ? ['system', 'cleanup'] : [])];
              const secondaryActive = secondary.includes(activeTab);
              const tabBtn = (tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  data-testid={`nav-tab-${tab}`}
                  className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                    activeTab === tab ? 'bg-blue-500/30 text-blue-200' : 'text-blue-300 hover:text-blue-200'
                  }`}
                >
                  {LABELS[tab] || t(tab)}
                </button>
              );
              return (
                <>
                  {primary.map(tabBtn)}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid="nav-more-btn"
                        className={`px-4 py-2 rounded-lg whitespace-nowrap flex items-center gap-1 ${
                          secondaryActive ? 'bg-blue-500/30 text-blue-200' : 'text-blue-300 hover:text-blue-200'
                        }`}
                      >
                        {secondaryActive ? LABELS[activeTab] : 'More'} <ChevronDown className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-slate-900 border-blue-400/20 text-blue-200">
                      {secondary.map((tab) => (
                        <DropdownMenuItem
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          data-testid={`nav-tab-${tab}`}
                          className="cursor-pointer focus:bg-blue-500/20 focus:text-blue-100"
                        >
                          {LABELS[tab] || t(tab)}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              );
            })()}
          </div>

          <div className="mt-6">
            {activeTab === 'hoy' && (
              <SystemOverview />
            )}
            {activeTab === 'calendar' && (
              <>
                {calendarError && (
                  <div
                    className="mb-4 flex items-center justify-between gap-4 rounded-xl border border-amber-500/40 bg-amber-900/20 px-4 py-3"
                    data-testid="calendar-load-error"
                  >
                    <p className="text-sm text-amber-200">
                      No pudimos cargar el calendario — el servidor no respondió por un momento. Tus datos están a salvo.
                    </p>
                    <button
                      onClick={() => { setCalendarError(false); refreshCalendarData(); }}
                      className="shrink-0 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-black hover:bg-amber-400 transition-colors"
                      data-testid="calendar-retry-btn"
                    >
                      Reintentar
                    </button>
                  </div>
                )}
                <AdminCalendar 
                  timeSlots={timeSlots}
                  bookings={bookings}
                  clients={clients}
                  workouts={workouts}
                  onRefresh={refreshCalendarData}
                  onCreateSlot={createTimeSlot}
                  t={t}
                />
              </>
            )}
            {activeTab === 'bookings' && (
              <UnifiedBookingsTab 
                bookings={bookings}
                clients={clients}
                exercises={exercises}
                onUpdateBooking={updateBooking}
                onRefresh={refreshCalendarData}
                userRole={user?.role}
                t={t}
                onViewClientWeek={(clientId, date) => {
                  // Navigate to client detail page with planner tab active
                  window.location.href = `/admin/clients/${clientId}?tab=planner&date=${date}`;
                }}
              />
            )}
            {activeTab === 'templates' && (
              <WorkoutTemplateLibrary 
                exercises={exercises}
                onRefresh={fetchData}
                t={t}
              />
            )}
            {activeTab === 'workouts' && (
              <WorkoutInstancesExplorer 
                user={user}
                exercises={exercises}
                onRefresh={fetchData}
              />
            )}
            {activeTab === 'clients' && (
              <ClientManager 
                clients={clients}
                onRefresh={fetchData}
                userRole={user?.role}
                t={t}
              />
            )}
            {activeTab === 'accounting' && (
              <AccountingDashboard />
            )}
            {activeTab === 'exercises' && (
              <ExerciseManager 
                exercises={exercises} 
                onRefresh={fetchData}
                onEdit={editExercise}
                onAdd={() => setAddingExercise(true)}
                language={language}
                t={t}
                onShowDetail={setSelectedExerciseForDetail}
              />
            )}
            
            {activeTab === 'cleanup' && user?.role === 'administrator' && (
              <GhostDataCleanup />
            )}

            {activeTab === 'system' && (
              <AdminPanel t={t} userRole={user?.role} />
            )}
          </div>
        </div>
      </div>

      {editingExercise && (
        <ExerciseEditModal
          exercise={editingExercise}
          onSave={handleEditExercise}
          onClose={() => setEditingExercise(null)}
          language={language}
          t={t}
          allMuscles={allMuscleGroups}
        />
      )}

      {addingExercise && (
        <ExerciseEditModal
          exercise={null}
          isCreate={true}
          onSave={handleCreateExercise}
          onClose={() => setAddingExercise(false)}
          language={language}
          t={t}
          allMuscles={allMuscleGroups}
        />
      )}

      {/* Scroll to Top Button - Only show on exercises tab */}
      {activeTab === 'exercises' && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            bottom: '32px',
            right: '32px',
            zIndex: 9999,
            backgroundColor: '#3B82F6',
            color: 'white',
            padding: '16px',
            borderRadius: '50%',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)';
            e.target.style.backgroundColor = '#2563EB';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.backgroundColor = '#3B82F6';
          }}
          title={t('scroll_to_top')}
        >
          ↑
        </button>
      )}

      {/* Exercise Detail Modal */}
      {selectedExerciseForDetail && (
        <ExerciseDetailModal
          exercise={selectedExerciseForDetail}
          onClose={() => setSelectedExerciseForDetail(null)}
          language={language}
          t={t}
        />
      )}
      
      {/* Workout View Modal - opened from Activity Feed */}
      {viewingWorkout && (
        <WorkoutViewModal
          booking={viewingWorkoutBooking}
          workout={viewingWorkout}
          prs={viewingWorkoutPRs}
          onClose={() => {
            setViewingWorkout(null);
            setViewingWorkoutBooking(null);
            setViewingWorkoutPRs([]);
          }}
          t={t}
        />
      )}
    </div>
  );
};



export default TrainerDashboard;
